/**
 * WAF対応のAPIクライアント
 * 中国からのアクセス時にWAFブロックされた場合、制限付きAPIにフォールバックする
 */

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * APIリクエスト時の認証ヘッダーを取得
 */
const getAuthHeaders = () => {
  let xsrfToken = document.cookie.split('; ').find(row => row.startsWith(process.env.REACT_APP_XSRF_TOKEN || 'XSRF-TOKEN'));
  if (xsrfToken) {
    xsrfToken = xsrfToken.split('=')[1];
  }
  let token = document.cookie.split('; ').find(row => row.startsWith('token'));
  if (token) {
    token = token.split('=')[1];
  }

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  if (xsrfToken) {
    headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
  }
  
  if (token) {
    headers['Bearer'] = decodeURIComponent(token);
  }

  return headers;
};

const buildQueryString = (params) => {
  if (!params) return '';
  if (typeof params === 'string') {
    return params.startsWith('?') ? params.slice(1) : params;
  }
  if (params instanceof URLSearchParams) {
    return params.toString();
  }
  if (typeof params === 'object') {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value);
      }
    });
    return searchParams.toString();
  }
  return '';
};

/**
 * URLからパス部分を抽出
 */
const getUrlPath = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch {
    // 相対URLの場合はそのまま返す
    return url.split('?')[0];
  }
};

/**
 * 通常APIのURLを制限版APIのURLに変換
 */
const getRestrictedApiUrl = (originalUrl) => {
  const urlPath = getUrlPath(originalUrl);
  const queryPart = originalUrl.includes('?') ? originalUrl.substring(originalUrl.indexOf('?')) : '';

  // /api/ship/{id} パターンの処理（先にチェック）
  const shipIdMatch = urlPath.match(/^\/api\/ship\/(\d+)$/);
  if (shipIdMatch) {
    const id = shipIdMatch[1];
    return `/api/ship-restricted/${id}${queryPart}`;
  }

  // /api/ship/lots/{lotId}/delete
  const lotDeleteMatch = urlPath.match(/^\/api\/ship\/lots\/([^/]+)\/delete$/);
  if (lotDeleteMatch) {
    const lotId = lotDeleteMatch[1];
    return `/api/ship/lots-restricted/${lotId}/delete${queryPart}`;
  }

  // /api/ship/lots/{lotId}
  const lotIdMatch = urlPath.match(/^\/api\/ship\/lots\/([^/]+)$/);
  if (lotIdMatch) {
    const lotId = lotIdMatch[1];
    return `/api/ship/lots-restricted/${lotId}${queryPart}`;
  }

  // その他のパターンマッピング
  const restrictedApiMap = {
    '/api/ship': '/api/ship-restricted',
    '/api/ship/hold': '/api/ship/hold-restricted',
    '/api/ship/ordered': '/api/ship/ordered-restricted', 
    '/api/ship/shipped': '/api/ship/shipped-restricted',
    '/api/ship/lots': '/api/ship/lots-restricted',
    '/api/user': '/api/user-restricted',
    '/api/settle-history': '/api/settle-history-restricted',
    '/api/settle-payment-history': '/api/settle-payment-history-restricted'
  };

  // クエリパラメータを含むURLの処理（より具体的なパスを優先）
  const restrictedEntries = Object.entries(restrictedApiMap)
    .sort((a, b) => b[0].length - a[0].length);

  for (const [pattern, replacement] of restrictedEntries) {
    if (urlPath === pattern || urlPath.startsWith(pattern + '/')) {
      return replacement + queryPart;
    }
  }

  return null; // 制限版APIが存在しない
};

/**
 * 制限状態の通知コールバック
 */
let restrictionNotificationCallbacks = [];

/**
 * 制限状態通知のコールバックを登録
 */
const onRestrictionDetected = (callback) => {
  restrictionNotificationCallbacks.push(callback);
};

/**
 * 制限状態を通知
 */
const notifyRestriction = (meta) => {
  restrictionNotificationCallbacks.forEach(callback => {
    try {
      callback(meta);
    } catch (error) {
      console.error('Restriction notification callback error:', error);
    }
  });
};

/**
 * WAF対応のfetch関数
 * @param {string} url - リクエストURL
 * @param {object} options - fetchオプション
 * @param {string} restrictedUrl - WAFブロック時のフォールバックURL（任意）
 * @returns {Promise} - レスポンス
 */
const fetchWithWAFFallback = async (url, options = {}, restrictedUrl = null) => {
  const defaultOptions = {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    },
    credentials: 'include',
    ...options
  };

  try {
    // 通常のAPIを試行
    const response = await fetch(url, defaultOptions);
    
    // ステータスが正常な場合はレスポンスを返す
    if (response.ok) {
      const data = await response.json();
      
      // 制限版APIレスポンスの検出と通知
      if (data.meta?.personal_info_restricted) {
        console.warn('Personal information restricted:', data.meta.message);
        notifyRestriction(data.meta);
      }
      
      return { ok: true, json: () => Promise.resolve(data) };
    }
    
    // 403エラー（WAFブロック）の場合、制限版APIを試行
    if (response.status === 403) {
      const fallbackUrl = restrictedUrl || getRestrictedApiUrl(url);
      
      if (fallbackUrl && getUrlPath(fallbackUrl) !== getUrlPath(url)) {
        console.warn(`WAF blocked detected (403). Trying restricted API: ${fallbackUrl}`);
        const fullFallbackUrl = fallbackUrl.startsWith('/') ? `${process.env.REACT_APP_API_URL}${fallbackUrl}` : fallbackUrl;
        return fetchWithWAFFallback(fullFallbackUrl, options);
      }
    }
    
    // その他のエラーの場合はそのまま返す
    return response;
    
  } catch (error) {
    // ネットワークエラーなど、制限付きAPIも試行
    const fallbackUrl = restrictedUrl || getRestrictedApiUrl(url);
    
    console.log('[DEBUG] CORS/Network Error:', { 
      originalUrl: url, 
      fallbackUrl, 
      errorName: error.name,
      errorMessage: error.message 
    });
    
    if (fallbackUrl && getUrlPath(fallbackUrl) !== getUrlPath(url) && (error.name === 'TypeError' || error.name === 'NetworkError')) {
      console.warn(`Network error detected for ${url}, trying restricted API: ${fallbackUrl}`);
      
      try {
        const fullFallbackUrl = fallbackUrl.startsWith('/') ? `${process.env.REACT_APP_API_URL}${fallbackUrl}` : fallbackUrl;
        const restrictedResponse = await fetch(fullFallbackUrl, defaultOptions);
        
        if (restrictedResponse.ok) {
          const restrictedData = await restrictedResponse.json();
          
          // 制限版APIレスポンスの通知
          if (restrictedData.meta?.personal_info_restricted) {
            console.warn('Personal information restricted (fallback):', restrictedData.meta.message);
            notifyRestriction(restrictedData.meta);
          }
          
          return { ok: true, json: () => Promise.resolve(restrictedData) };
        }
      } catch (restrictedError) {
        console.error('Both main and restricted APIs failed:', restrictedError);
      }
    }
    
    throw error;
  }
};

/**
 * 配送情報一覧を取得（WAF対応）
 */
export const fetchShipments = async (params = '', options = {}) => {
  const mainUrl = `${API_BASE_URL}/api/ship${params ? `?${params}` : ''}`;
  
  return fetchWithWAFFallback(mainUrl, options);
};

/**
 * 個別配送情報を取得（WAF対応）
 */
export const fetchShipment = async (id) => {
  const mainUrl = `${API_BASE_URL}/api/ship/${id}`;
  
  return fetchWithWAFFallback(mainUrl, {});
};

/**
 * ロットに紐づくWMSログを取得
 */
export const fetchLotWmsLogs = async (lotId, params = {}, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const query = buildQueryString(params);
  const mainUrl = `${API_BASE_URL}/api/ship/lots/${encodedId}/wms-logs${query ? `?${query}` : ''}`;

  return fetchWithWAFFallback(mainUrl, options);
};

/**
 * 入庫予定を再送信
 */
export const resyncEntryOrder = async (lotId, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const url = `${API_BASE_URL}/api/ship/lots/${encodedId}/resync-entry`;

  return fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  });
};

/**
 * 出庫指示を手動送信
 */
export const syncDeliveryOrder = async (lotId, { force = false, ...options } = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const params = force ? '?force=1' : '';
  const url = `${API_BASE_URL}/api/ship/lots/${encodedId}/sync-delivery${params}`;

  return fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  });
};

/**
 * 出庫指示をキャンセル（取消のみ。再作成は別途「出庫指示送信」で行う）
 */
export const cancelDeliveryOrder = async (lotId, { ...options } = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const url = `${API_BASE_URL}/api/ship/lots/${encodedId}/cancel-delivery`;

  return fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  });
};

/**
 * ロット一覧を取得（WAF対応）
 */
export const fetchShipLots = async (params = '', options = {}) => {
  const query = buildQueryString(params);
  const mainUrl = `${API_BASE_URL}/api/ship/lots${query ? `?${query}` : ''}`;

  return fetchWithWAFFallback(mainUrl, options);
};

/**
 * ロット詳細を取得（WAF対応）
 */
export const fetchShipLot = async (lotId, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const mainUrl = `${API_BASE_URL}/api/ship/lots/${encodedId}`;

  return fetchWithWAFFallback(mainUrl, options);
};

/**
 * ロットを新規作成
 */
export const createShipLot = async (payload = {}, options = {}) => {
  const mainUrl = `${API_BASE_URL}/api/ship/lots`;

  return fetchWithWAFFallback(mainUrl, {
    method: 'POST',
    body: JSON.stringify(payload),
    ...options,
  });
};

/**
 * 既存ロットを更新
 */
export const updateShipLot = async (lotId, payload = {}, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const mainUrl = `${API_BASE_URL}/api/ship/lots/${encodedId}`;

  return fetchWithWAFFallback(mainUrl, {
    method: 'POST',
    body: JSON.stringify(payload),
    ...options,
  });
};

/**
 * 既存ロットを削除
 */
export const deleteShipLot = async (lotId, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const mainUrl = `${API_BASE_URL}/api/ship/lots/${encodedId}/delete`;

  return fetchWithWAFFallback(mainUrl, {
    method: 'POST',
    ...options,
  });
};

/**
 * ロットを一括削除
 */
export const bulkDeleteLots = async (lotIds = [], options = {}) => {
  const mainUrl = `${API_BASE_URL}/api/ship/lots/bulk/delete`;

  return fetchWithWAFFallback(mainUrl, {
    method: 'POST',
    body: JSON.stringify({
      lot_ids: Array.isArray(lotIds) ? lotIds : [],
    }),
    ...options,
  });
};

/**
 * ロットのステータスを一括更新
 */
export const bulkUpdateLotStatus = async (lotIds = [], status, options = {}) => {
  const mainUrl = `${API_BASE_URL}/api/ship/lots/bulk/status`;

  return fetchWithWAFFallback(mainUrl, {
    method: 'POST',
    body: JSON.stringify({
      lot_ids: Array.isArray(lotIds) ? lotIds : [],
      status,
    }),
    ...options,
  });
};

/**
 * 保留中カード取得（WAF対応）
 */
export const fetchHoldCards = async () => {
  const url = `${API_BASE_URL}/api/ship/hold`;
  return fetchWithWAFFallback(url);
};

/**
 * 注文済みカード取得（WAF対応）
 */
export const fetchOrderedCards = async () => {
  const url = `${API_BASE_URL}/api/ship/ordered`;
  return fetchWithWAFFallback(url);
};

/**
 * 発送済みカード取得（WAF対応）
 */
export const fetchShippedCards = async () => {
  const url = `${API_BASE_URL}/api/ship/shipped`;
  return fetchWithWAFFallback(url);
};

/**
 * ユーザー一覧取得（WAF対応）
/**
 * ユーザー一覧を取得（WAF対応）
 */
export const fetchUsers = async (params = '') => {
  const mainUrl = `${API_BASE_URL}/api/user${params ? `?${params}` : ''}`;
  
  try {
    const response = await fetchWithWAFFallback(mainUrl, {});
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to fetch users:', error);
    throw error;
  }
};

/**
 * 決済履歴を取得（WAF対応）
 */
export const fetchSettleHistory = async (params = '') => {
  const url = `${API_BASE_URL}/api/settle-history${params ? `?${params}` : ''}`;
  return fetchWithWAFFallback(url);
};

// 抽卡交易（draw_transactions）管理 #57
export const fetchDrawTransactions = async (params = '') => {
  const url = `${API_BASE_URL}/api/draw-transactions/search${params ? `?${params}` : ''}`;
  return fetchWithWAFFallback(url);
};

// 送料決済（発送依頼時の配送費支払い）一覧
export const fetchShipPayments = async (params = '') => {
  const url = `${API_BASE_URL}/api/ship-payments/search${params ? `?${params}` : ''}`;
  return fetchWithWAFFallback(url);
};

// 返金キュー（refund_queue）管理 #61
export const fetchRefundQueue = async (params = '') => {
  const url = `${API_BASE_URL}/api/refund-queue${params ? `?${params}` : ''}`;
  return fetchWithWAFFallback(url);
};

export const completeRefund = async (id) => {
  const url = `${API_BASE_URL}/api/refund-queue/${id}/complete`;
  return fetchWithWAFFallback(url, { method: 'POST', body: JSON.stringify({}) });
};

/**
 * 支払い履歴取得（WAF対応）
 */
export const fetchSettlePaymentHistory = async (params = '') => {
  const url = `${API_BASE_URL}/api/settle-payment-history${params ? `?${params}` : ''}`;
  return fetchWithWAFFallback(url);
};

/**
 * CSVダウンロード（個人情報含むため制限なし）
 */
export const downloadShipmentCSV = async (shipmentIds, type = '') => {
  const typeParam = type ? `/${type}` : '';
  const url = `${API_BASE_URL}/api/ship/download/${shipmentIds}${typeParam}`;
  
  return fetchWithWAFFallback(url);
};

/**
 * 配送ステータス変更
 */
export const updateShipmentStatus = async (id, data) => {
  const url = `${API_BASE_URL}/api/ship/status/${id}`;
  
  return fetchWithWAFFallback(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

/**
 * 発送分割: 選んだカードを新しい発送IDへ切り出す
 * （個人情報を返さないため WAF 例外 /split に載せており、フォールバック不要）
 */
export const splitShipment = async (id, payload) => {
  const url = `${API_BASE_URL}/api/ship/${id}/split`;

  return fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
};

/**
 * 発送予定日の手入力（null で従来の自動計算に戻す）
 */
export const updateShipmentExpectedShipDate = async (id, expectedShipDate) => {
  const url = `${API_BASE_URL}/api/ship/${id}/expected-ship-date`;

  return fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ expected_ship_date: expectedShipDate || null }),
  });
};

/**
 * 制限状態通知のコールバック登録をエクスポート
 */
export { onRestrictionDetected };

/**
 * 個人情報制限状態をチェックするユーティリティ
 */
export const isPersonalInfoRestricted = (responseData) => {
  return responseData && responseData.meta?.personal_info_restricted === true;
};

/**
 * 制限された個人情報を表示用にフォーマット
 */
export const formatRestrictedPersonalInfo = (data, fieldName) => {
  if (isPersonalInfoRestricted(data)) {
    switch (fieldName) {
      case 'client_name':
      case 'first_name':
      case 'last_name':
        return '***';
      case 'client_address':
        return '***';
      case 'note':
        return '***';
      default:
        return '***';
    }
  }
  return data[fieldName];
};

/**
 * ロット単位でロケーションズ用CSVをダウンロード（バイナリZIPを返すためWAFフォールバック不使用）
 */
export const downloadLotLocations = async (lotId, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const url = `${API_BASE_URL}/api/ship/lots/${encodedId}/download/locations`;

  return fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  });
};

/**
 * ロット単位でインボイスPDFをダウンロード
 */
export const downloadLotInvoice = async (lotId, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const url = `${API_BASE_URL}/api/ship/lots/${encodedId}/download/invoice`;

  return fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  });
};

/**
 * ロット単位で箱单(パッキングリスト)PDFをダウンロード
 */
export const downloadLotPackingList = async (lotId, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const url = `${API_BASE_URL}/api/ship/lots/${encodedId}/download/packing-list`;

  return fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  });
};

export const downloadLotPurchaseDetail = async (lotId, options = {}) => {
  const encodedId = encodeURIComponent(lotId);
  const url = `${API_BASE_URL}/api/ship/lots/${encodedId}/download/purchase-detail`;

  return fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  });
};

/**
 * ユーザー単位のガチャ当選履歴 CSV をダウンロード（/api/user* は WAF 地域制限対象のため別パス）
 */
export const downloadUserGachaHistory = async (userId, options = {}) => {
  const encodedId = encodeURIComponent(userId);
  const url = `${API_BASE_URL}/api/draw-history/${encodedId}/download`;

  return fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
    ...options,
  });
};

export default {
  fetchShipments,
  fetchShipment,
  fetchShipLots,
  fetchShipLot,
  createShipLot,
  updateShipLot,
  deleteShipLot,
  updateShipmentStatus,
  fetchLotWmsLogs,
  resyncEntryOrder,
  syncDeliveryOrder,
  downloadLotLocations,
  downloadLotInvoice,
  downloadLotPackingList,
  downloadLotPurchaseDetail,
  downloadUserGachaHistory,
  fetchHoldCards,
  fetchOrderedCards,
  fetchShippedCards,
  fetchUsers,
  fetchSettleHistory,
  fetchSettlePaymentHistory,
  downloadShipmentCSV,
  isPersonalInfoRestricted,
  formatRestrictedPersonalInfo,
  onRestrictionDetected
};
